import axios from "axios";

// Backend URL from env (kept simple). For production deployments where the
// build-time env points to preview, prefer the current origin when hosts differ.
const ENV_URL = process.env.REACT_APP_BACKEND_URL || "";
const isBrowser = typeof window !== "undefined";
const sameOrigin = isBrowser ? window.location.origin : "";
let BACKEND_URL = ENV_URL;
if (isBrowser && ENV_URL) {
  try {
    const envHost = new URL(ENV_URL).host;
    if (envHost !== window.location.host) BACKEND_URL = sameOrigin;
  } catch { BACKEND_URL = sameOrigin; }
}
if (!BACKEND_URL) BACKEND_URL = sameOrigin;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API });

/**
 * Returns the canonical site base URL (e.g. https://spravnysmer.eu) with no trailing slash.
 * Falls back to window.location.origin. Always guarantees the result starts with http(s)://.
 */
export function canonicalBase() {
  const raw = (typeof window !== "undefined" && window.__siteSettings?.seo_canonical_base)
    || (typeof window !== "undefined" ? window.location.origin : "");
  const trimmed = String(raw || "").trim().replace(/\/+$/, "");
  if (!trimmed) return "";
  return /^https?:\/\//i.test(trimmed) ? trimmed : "https://" + trimmed.replace(/^\/+/, "");
}

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("ss_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  // Auto-attach ?lang= to every GET so backend can translate dynamic content.
  // Skipped for admin endpoints since admin always edits in Czech.
  try {
    const lang = (localStorage.getItem("ss_lang") || "cs").toLowerCase();
    const isAdmin = (config.url || "").includes("/admin/");
    if (lang !== "cs" && !isAdmin) {
      config.params = { ...(config.params || {}), lang };
    }
  } catch (e) { /* ignore */ }
  return config;
});

api.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err?.response?.status === 401) {
      // optional auto-logout
    }
    return Promise.reject(err);
  }
);

export const formatPrice = (v, currency = "CZK") =>
  new Intl.NumberFormat("cs-CZ", { style: "currency", currency, maximumFractionDigits: 0 }).format(v || 0);

// Storefront price formatter. Converts a CZK amount to the customer's currency
// (chosen by current i18n language) using ČNB FX rates cached by /lib/currency.js.
// Admin/back-office should keep using `formatPrice` (always CZK).
export const formatPriceLocal = (v) => {
  try {
    const lang = (localStorage.getItem("ss_lang") || "cs").toLowerCase();
    if (lang === "cs") return formatPrice(v);
    const map = { cs: "CZK", sk: "EUR", hu: "HUF", en: "EUR" };
    const currency = map[lang] || "CZK";
    const cached = JSON.parse(localStorage.getItem("ss_fx_rates_v1") || "null");
    const rates = cached?.rates || {};
    const rate = rates[currency];
    let amount = v || 0;
    if (rate && rate > 0 && currency !== "CZK") amount = amount / rate;
    const localeMap = { CZK: "cs-CZ", EUR: "en-IE", HUF: "hu-HU", USD: "en-US" };
    const decimals = currency === "HUF" || currency === "CZK" ? 0 : 2;
    return new Intl.NumberFormat(localeMap[currency] || "cs-CZ", {
      style: "currency",
      currency,
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(amount);
  } catch (e) {
    return formatPrice(v);
  }
};

// Rewrite relative asset URLs to the current backend origin. Absolute URLs
// (R2 public bucket, CDNs, customer assets, stock photos) are returned
// unchanged — they already point to the canonical place and re-hosting
// them on the app domain only produces broken images.
export const fixAssetUrl = (url) => {
  if (!url || typeof url !== "string") return url;
  // Relative path → prefix current backend
  if (url.startsWith("/")) return `${BACKEND_URL}${url}`;
  // Absolute URL → leave as-is.
  return url;
};
